import React, { useState } from 'react';
import { Discount } from '@ajat/design-system/ShoppingCart';
import { ComponentPage, DemoCard } from '../../shared';

const VALID_CODE = 'SHOP15';

export const meta = {
  component: Discount,
  title: 'Discount',
  group: 'Shopping Cart',
  importPath: '@ajat/design-system/ShoppingCart',
  figmaNode: '3972:2861',
  description:
    'Promo-code field with three states. Default is an action Input; submitted becomes a read-only row with a check and a remove button; error keeps the field editable and pre-filled so the customer can correct the typo rather than retype it.',
  props: [
    ['label', 'string', "'Discount code'", 'Placeholder / label'],
    ['code', 'string', "''", 'The code — shown read-only when submitted, pre-filled when error'],
    ['submitted', 'bool', 'false', 'Applied successfully — switches to the read-only row'],
    ['error', 'bool', 'false', 'Invalid code — keeps the field editable'],
    ['discountLabel', 'string', "''", 'Green success message, e.g. "15% DISCOUNT"'],
    ['errorLabel', 'string', "''", 'Red error message, e.g. "INVALID CODE"'],
    ['onSubmit', 'func', '—', 'Fires with the entered code string'],
    ['onRemove', 'func', '—', 'Fires when the applied code is removed'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  usage: [
    {
      title: 'Wired to the cart API',
      code: `import { Discount } from '@ajat/design-system/ShoppingCart';

const { discount, applyDiscount, removeDiscount } = useCart(brandSlug);
const [error, setError] = useState(null);

<Discount
  label="Rabatkode"
  code={discount?.code ?? error?.code ?? ''}
  submitted={Boolean(discount)}
  error={Boolean(error)}
  discountLabel={discount ? \`\${discount.percent}% RABAT\` : ''}
  errorLabel={error ? 'UGYLDIG KODE' : ''}
  onSubmit={async (code) => {
    setError(null);
    try { await applyDiscount(code); }
    catch { setError({ code }); }     // keeps the code in the field
  }}
  onRemove={removeDiscount}
/>`,
    },
  ],
};

export default function DiscountPage() {
  const [applied, setApplied] = useState(null);
  const [failed, setFailed] = useState(null);

  const handleSubmit = (code) => {
    if (code.trim().toUpperCase() === VALID_CODE) {
      setApplied(code.trim().toUpperCase());
      setFailed(null);
    } else {
      setFailed(code);
      setApplied(null);
    }
  };

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Default">
        <div className="max-w-md">
          <Discount label="Discount code" onSubmit={() => {}} />
        </div>
      </DemoCard>

      <DemoCard title="Submitted" hint="read-only, with a remove button">
        <div className="max-w-md">
          <Discount code="SHOP15" submitted discountLabel="15% DISCOUNT" onRemove={() => {}} />
        </div>
      </DemoCard>

      <DemoCard title="Error" hint="still editable, code preserved">
        <div className="max-w-md">
          <Discount code="WRONG" error errorLabel="INVALID CODE" onSubmit={() => {}} />
        </div>
      </DemoCard>

      <DemoCard title="Interactive" hint={`try "${VALID_CODE}" — anything else fails`}>
        <div className="max-w-md">
          <Discount
            label="Discount code"
            code={applied || failed || ''}
            submitted={Boolean(applied)}
            error={Boolean(failed)}
            discountLabel={applied ? '15% DISCOUNT' : ''}
            errorLabel={failed ? 'INVALID CODE' : ''}
            onSubmit={handleSubmit}
            onRemove={() => { setApplied(null); setFailed(null); }}
          />
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
